import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Incident, CreateIncidentPayload, IncidentStatus } from '../../types';
import { API_BASE_URL } from '../../lib/utils';

interface IncidentState {
  incidents: Incident[];
  selectedIncident: Incident | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    status: IncidentStatus | 'all';
    severity: string;
    category: string;
    search: string;
  };
}

const initialState: IncidentState = {
  incidents: [],
  selectedIncident: null,
  isLoading: false,
  error: null,
  filters: {
    status: 'all',
    severity: 'all',
    category: 'all',
    search: '',
  },
};

export const fetchIncidents = createAsyncThunk<Incident[]>(
  'incidents/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('c2_token');
      const response = await fetch(`${API_BASE_URL}/incidents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return rejectWithValue('Failed to fetch incidents');
      const data = await response.json();
      return data.data;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

export const createIncident = createAsyncThunk<Incident, CreateIncidentPayload>(
  'incidents/create',
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('c2_token');
      const response = await fetch(`${API_BASE_URL}/incidents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) return rejectWithValue('Failed to create incident');
      const data = await response.json();
      return data.data;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

export const updateIncidentStatus = createAsyncThunk<
  Incident,
  { id: string; status: IncidentStatus }
>(
  'incidents/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('c2_token');
      const response = await fetch(`${API_BASE_URL}/incidents/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) return rejectWithValue('Failed to update status');
      const data = await response.json();
      return data.data;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

const incidentSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    selectIncident(state, action: PayloadAction<Incident | null>) {
      state.selectedIncident = action.payload;
    },
    setFilter(state, action: PayloadAction<{ key: string; value: string }>) {
      const { key, value } = action.payload;
      (state.filters as Record<string, string>)[key] = value;
    },
    incidentUpdated(state, action: PayloadAction<Partial<Incident> & { id: string }>) {
      const index = state.incidents.findIndex((i) => i.id === action.payload.id);
      if (index !== -1) {
        state.incidents[index] = { ...state.incidents[index], ...action.payload };
      }
      if (state.selectedIncident?.id === action.payload.id) {
        state.selectedIncident = { ...state.selectedIncident, ...action.payload };
      }
    },
    incidentCreatedFromSocket(state, action: PayloadAction<Incident>) {
      const exists = state.incidents.find((i) => i.id === action.payload.id);
      if (!exists) {
        state.incidents.unshift(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIncidents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIncidents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.incidents = action.payload;
      })
      .addCase(fetchIncidents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createIncident.fulfilled, (state, action) => {
        state.incidents.unshift(action.payload);
      })
      .addCase(updateIncidentStatus.fulfilled, (state, action) => {
        const index = state.incidents.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.incidents[index] = action.payload;
        }
        if (state.selectedIncident?.id === action.payload.id) {
          state.selectedIncident = action.payload;
        }
      });
  },
});

export const {
  selectIncident,
  setFilter,
  incidentUpdated,
  incidentCreatedFromSocket,
} = incidentSlice.actions;
export default incidentSlice.reducer;
